import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaReply, FaTrashAlt, FaComments, FaUserCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../../../Services/api';
import { useOperador } from '../../../../../Hooks/useOperador';

import './AnaliseComentarios.css';

const getAnaliseTimestamp = (analise) => {
  if (!analise?.dataCadastro) return 0;
  const parsed = new Date(analise.dataCadastro).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const montarArvoreAnalises = (lista = []) => {
  const mapa = new Map();
  const raizes = [];

  lista.forEach((analise) => {
    mapa.set(analise.IDAnalise, { ...analise, children: [] });
  });

  mapa.forEach((analise) => {
    const parentId = analise.IDAnalisePai;
    if (parentId && mapa.has(parentId)) {
      mapa.get(parentId).children.push(analise);
      return;
    }
    raizes.push(analise);
  });

  const ordenar = (itens) => {
    itens.sort((a, b) => getAnaliseTimestamp(a) - getAnaliseTimestamp(b));
    itens.forEach((item) => ordenar(item.children));
  };

  ordenar(raizes);

  return raizes;
};

const redimensionarAnalises = (raizes = []) => {
  const saida = [];

  const percorrer = (analise, depth) => {
    saida.push({ analise, depth });
    if (!analise.children?.length) return;
    analise.children.forEach((child) => percorrer(child, depth + 1));
  };

  raizes.forEach((analise) => percorrer(analise, 0));
  return saida;
};

export const AnaliseComentariosTitle = ({ questaoSelecionada }) => (
  <div className="auditoria-checklist-comentarios-header">
    <h3>
      <FaComments className="auditoria-checklist-title-icon" />
      Análises
    </h3>
    <span className="auditoria-checklist-comentarios-subtitle">
      {questaoSelecionada?.titulo
        ? `Questao: ${questaoSelecionada.titulo}`
        : 'Selecione uma questao'}
    </span>
  </div>
);

const AnaliseComentarios = ({ questaoSelecionada, onHasAnalisesChange }) => {
  const [comentarios, setComentarios] = useState([]);
  const [comentariosLoading, setComentariosLoading] = useState(false);
  const [comentariosErro, setComentariosErro] = useState('');
  const [comentarioTexto, setComentarioTexto] = useState('');
  const [comentarioSalvando, setComentarioSalvando] = useState(false);
  const [respostaAtivaId, setRespostaAtivaId] = useState(null);
  const [respostaTexto, setRespostaTexto] = useState('');
  const [operadoresPorId, setOperadoresPorId] = useState({});
  const operadoresCacheRef = useRef({});
  const { IDOperador, nome, permissaoPrincipal } = useOperador();
  const isAdmin = permissaoPrincipal === 99;
  const analiseHabilitada = questaoSelecionada?.habilitaAnalise === 1;

  const buscarNomeOperador = useCallback(async (idOperador) => {
    try {
      const response = await api.get(`/operador/${idOperador}`);
      return response.data?.Operador?.nome || response.data?.nome || null;
    } catch (error) {
      console.error('Erro ao carregar operador', error);
      return null;
    }
  }, []);

  const carregarOperadores = useCallback(
    async (lista) => {
      const ids = Array.from(
        new Set(
          (lista || [])
            .map((comentario) => {
              const rawId = comentario?.IDOperador;
              const normalized = Number(String(rawId ?? '').trim());
              return Number.isNaN(normalized) ? null : normalized;
            })
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      );

      const faltantes = ids.filter((id) => !operadoresCacheRef.current[id]);
      if (!faltantes.length) return;

      try {
        const responses = await Promise.all(
          faltantes.map(async (id) => {
            const nomeOperador = await buscarNomeOperador(id);
            return { id, nome: nomeOperador };
          }),
        );

        const novos = responses.reduce((acc, item) => {
          if (!item?.id) return acc;
          acc[item.id] = item.nome || `Operador ${item.id}`;
          return acc;
        }, {});

        operadoresCacheRef.current = {
          ...operadoresCacheRef.current,
          ...novos,
        };
        setOperadoresPorId((prev) => ({ ...prev, ...novos }));
      } catch (error) {
        console.error('Erro ao carregar operadores', error);
      }
    },
    [buscarNomeOperador],
  );

  const carregarComentarios = useCallback(async () => {
    if (!questaoSelecionada?.IDQuestao_Secao) {
      setComentarios([]);
      onHasAnalisesChange?.(false);
      return;
    }
    if (!analiseHabilitada) {
      setComentarios([]);
      setComentariosErro('');
      onHasAnalisesChange?.(false);
      return;
    }

    try {
      setComentariosLoading(true);
      setComentariosErro('');
      const response = await api.get('/chkanalises', {
        params: {
          page: 1,
          limit: 200,
          IDQuestao_Secao: questaoSelecionada.IDQuestao_Secao,
        },
      });

      const data = response.data?.data || [];
      const ativos = data.filter((comentario) => comentario.situacao === 1);
      onHasAnalisesChange?.(ativos.length > 0);
      setComentarios(data);
      carregarOperadores(data);
    } catch (error) {
      console.error('Erro ao carregar comentarios', error);
      setComentariosErro('Nao foi possivel carregar os comentarios.');
      setComentarios([]);
      onHasAnalisesChange?.(false);
    } finally {
      setComentariosLoading(false);
    }
  }, [
    questaoSelecionada,
    carregarOperadores,
    onHasAnalisesChange,
    analiseHabilitada,
  ]);

  useEffect(() => {
    carregarComentarios();
  }, [carregarComentarios]);

  useEffect(() => {
    setRespostaAtivaId(null);
    setRespostaTexto('');
    setComentarioTexto('');
  }, [questaoSelecionada?.IDQuestao_Secao]);

  const formatarDataComentario = (valor) => {
    if (!valor) return 'Sem data';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return String(valor);
    return data.toLocaleString();
  };

  const comentariosPlanos = useMemo(() => {
    if (!Array.isArray(comentarios)) return [];
    const ativos = comentarios.filter(
      (comentario) => comentario.situacao === 1,
    );
    const arvore = montarArvoreAnalises(ativos);
    return redimensionarAnalises(arvore);
  }, [comentarios]);

  const criarComentario = useCallback(
    async (texto, parentId = null) => {
      const descricao = (texto || '').trim().slice(0, 500);
      if (!descricao) return;
      if (!IDOperador || !questaoSelecionada?.IDQuestao_Secao) return;

      try {
        setComentarioSalvando(true);
        await api.post('/chkanalise', {
          IDQuestao_Secao: questaoSelecionada.IDQuestao_Secao,
          IDOperador,
          IDAnalisePai: parentId,
          descricao,
          tipo: 1,
          situacao: 1,
        });
        setComentarioTexto('');
        setRespostaAtivaId(null);
        setRespostaTexto('');
        await carregarComentarios();
      } catch (error) {
        console.error('Erro ao salvar comentário', error);
        setComentariosErro('Nao foi possivel salvar o comentário.');
      } finally {
        setComentarioSalvando(false);
      }
    },
    [IDOperador, questaoSelecionada, carregarComentarios],
  );

  const handleEnviarComentario = (event) => {
    event.preventDefault();
    criarComentario(comentarioTexto, null);
  };

  const handleEnviarResposta = (event) => {
    event.preventDefault();
    if (!respostaAtivaId) return;
    criarComentario(respostaTexto, respostaAtivaId);
  };

  const getAutorComentario = (analise) => {
    if (analise?.IDOperador === IDOperador) {
      return nome || 'Você';
    }
    const operadorId = Number(String(analise?.IDOperador ?? '').trim());
    if (!Number.isFinite(operadorId) || operadorId <= 0) return 'Operador -';
    return operadoresPorId[operadorId] || `Operador ${operadorId}`;
  };

  const renderComentario = (analise, nivel = 0) => {
    const isReplying = respostaAtivaId === analise.IDAnalise;
    const podeExcluir = isAdmin || analise?.IDOperador === IDOperador;
    const parentId = analise?.IDAnalisePai;
    const parent = parentId
      ? comentarios.find((item) => item.IDAnalise === parentId)
      : null;

    return (
      <div
        key={analise.IDAnalise}
        id={`comentario-${analise.IDAnalise}`}
        className={`auditoria-comentario ${nivel > 0 ? 'is-reply' : ''}`}
        style={nivel > 0 ? { '--reply-depth': nivel } : undefined}
      >
        <div className="auditoria-comentario-header">
          <span className="auditoria-comentario-autor">
            <FaUserCircle aria-hidden="true" />
            {getAutorComentario(analise)}
          </span>
          <span className="auditoria-comentario-data">
            {formatarDataComentario(analise.dataCadastro)}
          </span>
        </div>
        {parent && (
          <div className="auditoria-comentario-link">
            Em resposta a{' '}
            <a href={`#comentario-${parent.IDAnalise}`}>
              {getAutorComentario(parent)}
            </a>
          </div>
        )}
        <p className="auditoria-comentario-texto">{analise.descricao}</p>
        <div className="auditoria-comentario-acoes">
          <button
            type="button"
            className="auditoria-comentario-btn auditoria-comentario-btn-reply"
            onClick={() => {
              setRespostaAtivaId(analise.IDAnalise);
              setRespostaTexto('');
            }}
          >
            <FaReply aria-hidden="true" />
            Responder
          </button>
          {podeExcluir && (
            <button
              type="button"
              className="auditoria-comentario-btn auditoria-comentario-btn-delete"
              onClick={async () => {
                if (!questaoSelecionada?.IDQuestao_Secao) return;
                if (!analise?.IDAnalise) return;

                const result = await Swal.fire({
                  title: 'Confirmar exclusão?',
                  text: 'Este comentário será excluído.',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Sim, excluir',
                  cancelButtonText: 'Cancelar',
                  cancelButtonColor: "var(--color-btnCancelar-danger)",
                  customClass: {
                    cancelButton: "swal-cancel-danger",
                  },
                });

                if (!result.isConfirmed) return;

                try {
                  setComentarioSalvando(true);
                  await api.put(`/chkanalise/${analise.IDAnalise}`, {
                    IDQuestao_Secao: questaoSelecionada.IDQuestao_Secao,
                    IDOperador: analise.IDOperador,
                    IDAnalisePai: analise.IDAnalisePai ?? null,
                    descricao: analise.descricao,
                    tipo: analise.tipo,
                    situacao: 0,
                  });
                  await carregarComentarios();
                  Swal.fire(
                    'Excluído!',
                    'Comentário excluído com sucesso.',
                    'success',
                  );
                } catch (error) {
                  console.error('Erro ao excluir comentário', error);
                  setComentariosErro('Nao foi possivel excluir o comentário.');
                  Swal.fire(
                    'Erro',
                    'Nao foi possivel excluir o comentário.',
                    'error',
                  );
                } finally {
                  setComentarioSalvando(false);
                }
              }}
              disabled={comentarioSalvando}
            >
              <FaTrashAlt aria-hidden="true" />
              Excluir
            </button>
          )}
        </div>

        {isReplying && (
          <form
            className="auditoria-comentario-form auditoria-comentario-form-reply"
            onSubmit={handleEnviarResposta}
          >
            <textarea
              className="auditoria-comentario-textarea"
              placeholder="Escreva uma resposta..."
              value={respostaTexto}
              onChange={(event) => setRespostaTexto(event.target.value)}
              maxLength={500}
            />
            <div className="auditoria-comentario-form-actions">
              <span className="auditoria-comentario-contador">
                {respostaTexto.length}/500
              </span>
              <button
                type="button"
                className="auditoria-comentario-cancelar"
                onClick={() => {
                  setRespostaAtivaId(null);
                  setRespostaTexto('');
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="auditoria-comentario-submit"
                disabled={
                  comentarioSalvando || !respostaTexto.trim() || !IDOperador
                }
              >
                Enviar
              </button>
            </div>
          </form>
        )}
      </div>
    );
  };

  return (
    <div className="auditoria-checklist-comentarios">
      <div className="auditoria-checklist-comentarios-body">
        {!questaoSelecionada && (
          <p className="auditoria-comentario-placeholder">
            Selecione uma questao para ver e criar comentários.
          </p>
        )}
        {questaoSelecionada && !analiseHabilitada && (
          <p className="auditoria-comentario-placeholder">
            Esta questão não permite análises.
          </p>
        )}
        {questaoSelecionada && analiseHabilitada && (
          <div className="auditoria-comentarios-container">
            {comentariosLoading && (
              <p className="auditoria-comentario-placeholder">
                Carregando comentários...
              </p>
            )}
            {!comentariosLoading && comentariosErro && (
              <p className="auditoria-comentario-placeholder">
                {comentariosErro}
              </p>
            )}
            {!comentariosLoading &&
              !comentariosErro &&
              comentariosPlanos.length === 0 && (
                <p className="auditoria-comentario-placeholder">
                  Nenhum comentário ainda.
                </p>
              )}

            {!comentariosLoading &&
              !comentariosErro &&
              comentariosPlanos.length > 0 && (
                <div className="auditoria-comentarios-list">
                  {comentariosPlanos.map(({ analise, depth }) =>
                    renderComentario(analise, depth),
                  )}
                </div>
              )}

            <form
              className="auditoria-comentario-form"
              onSubmit={handleEnviarComentario}
            >
              <textarea
                className="auditoria-comentario-textarea"
                placeholder="Escreva um comentário..."
                value={comentarioTexto}
                onChange={(event) => setComentarioTexto(event.target.value)}
                maxLength={500}
              />
              <div className="auditoria-comentario-form-actions">
                <span className="auditoria-comentario-contador">
                  {comentarioTexto.length}/500
                </span>
                <button
                  type="submit"
                  className="auditoria-comentario-submit"
                  disabled={
                    comentarioSalvando || !comentarioTexto.trim() || !IDOperador
                  }
                >
                  Enviar
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnaliseComentarios;
